import { ManageForm } from '@components/forms/ManageForm';
import { useQuery } from '@hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { SystemEffects } from './SystemEffects';
import { Query } from '@features/redux/apis/utils';
import { Grid, Typography } from '@mui/material';
import { useSystemMutation, useSystemByIdQuery } from '@features/redux/apis/system';
import { AppRoutes } from '@consts/routes';
import { SYSTEM_STATUS_OPTIONS, SystemModelMap, SystemStatus } from '@consts/systems';
import { FormActions } from '@components/forms/FormActions';
import { useSelector } from 'react-redux';

import { OrgDropDowns } from '@components/forms/dropdowns/OrgDropdowns';
import { Validate } from '@utils/validate';
import { ViewSystemLocation } from './ViewSystemLocation';
import { useLocationMapQuery, useNameMapQuery } from '@features/redux/apis/org';
import { FormSelect } from '@components/forms/dropdowns/base/FormSelect';
import { FormDisabledText } from '@components/forms/text/base/FormDisabledText';
import { LastConnected } from '@components/LastConnected';
import { ForceOffset } from './ForceOffset';

import type { RootState } from '@features/redux/store';
import { FormTextArea } from '@components/forms/text/base/FormTextArea';
import type { System as SystemType } from '@api/system';

// ? Don't use withDraft unless we need to. We don't need to
// ? create a draft in the SystemEffects component, for example.
const useSystem = Query.withDraft(
    useSystemByIdQuery,
    {
        StationID: null,
        LocationID: null,
        CompanyID: null,
        ResellerID: null,
        Model: null,
        Hostname: '',
        CreatedDate: '',
        SerialNum: null,
        Status: SystemStatus.Active,
        LastConnected: null,
        ConfigHash: null,
        ProductID: null,
        Notes: '',
    },
    (fetched, init) => {
        const next = structuredClone(fetched);
        for (const k in init) {
            const key = k as keyof SystemType;
            if (!next[key]) {
                (next[key] as any) = init[key];
            }
        }

        return next;
    }
);

export const System = () => {
    const navigate = useNavigate();
    const stationId = useQuery().get('id')!;

    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const { isLoading: nameMapLoading } = useNameMapQuery({});
    const { isLoading: locationMapLoading } = useLocationMapQuery({});

    const { data: system, isLoading: systemQueryLoading, draft } = useSystem({ id: stationId! });
    const [draftState] = draft;

    const [mutate, { isLoading: mutationLoading }] = useSystemMutation({
        fixedCacheKey: 'SystemMutation',
    });

    // ? It's an RC2/3 if the ModelID is 0 or null. 1 is RCMini.
    const isRapidCut2or3 = !system?.Model;

    const isReassignment =
        system?.LocationID !== draftState?.LocationID ||
        system?.CompanyID !== draftState?.CompanyID ||
        system?.ResellerID !== draftState?.ResellerID;

    const systemDataLoading = systemQueryLoading || mutationLoading;
    const orgDataLoading = nameMapLoading || locationMapLoading;

    return (
        <>
            <SystemEffects />

            <ManageForm title={'RapidCut System Information'} loading={orgDataLoading || systemDataLoading} size={12}>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Grid container spacing={2} marginTop='10px'>
                        <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography fontWeight='bold'>System:</Typography>

                            <LastConnected timestamp={system?.LastConnected} />
                        </Grid>

                        <FormDisabledText
                            size={12}
                            label='Model'
                            value={SystemModelMap[system?.Model as any] || 'N/A'}
                        />

                        {role.isAdmin && (
                            <FormDisabledText
                                size={12}
                                label='System ID'
                                value={system?.StationID?.toString() || 'N/A'}
                            />
                        )}

                        <FormDisabledText size={12} label='Serial Number' value={system?.SerialNum || 'N/A'} />

                        <FormSelect
                            label='Status'
                            size={12}
                            draft={draft}
                            stateKey='Status'
                            options={SYSTEM_STATUS_OPTIONS}
                        />

                        <Grid item md={12} sm={12}>
                            <Typography fontWeight='bold'>Reassign this system:</Typography>
                            <Typography>
                                Select a company, reseller (optional), and location from the dropdown below that you
                                wish to assign to this RapidCut system.
                            </Typography>
                            <Typography fontWeight='bold' fontSize='12px'>
                                *Note that RC2/RC3 systems cannot currently be reassigned
                            </Typography>
                        </Grid>

                        <OrgDropDowns
                            size={12}
                            draft={draft}
                            options={{
                                companies: true,
                                resellers: true,
                                locations: true,
                            }}
                        />

                        {role.isAdmin && role.stores.edit && (
                            <FormTextArea
                                helperText='Notes (Internal-only)'
                                placeholder='Notes (Internal-only)'
                                draft={draft}
                                stateKey='Notes'
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <Grid container spacing={2} marginTop='10px'>
                        <Grid item xs={12} sm={12} md={12}>
                            <Typography fontWeight='bold'>Assigned system location:</Typography>
                        </Grid>

                        <ViewSystemLocation locationId={system?.LocationID} />
                    </Grid>

                    <ForceOffset serialNumber={system?.SerialNum} productId={system?.ProductID} />
                </Grid>

                <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                    <FormActions
                        onSave={() => {
                            mutate({
                                system: draftState,
                            });
                        }}
                        tooltip={
                            isReassignment && isRapidCut2or3
                                ? "Can't reassign RC2/RC3 systems!"
                                : !Validate.system(draftState)
                                ? 'Please fill all necessary fields.'
                                : ''
                        }
                        onCancel={() => navigate(AppRoutes.SystemManagement)}
                        disabled={(isReassignment && isRapidCut2or3) || !Validate.system(draftState)}
                        isUpdate={true}
                    />
                </Grid>
            </ManageForm>
        </>
    );
};
